import React, { useEffect, useState, useRef } from 'react';
import { Container, Row, Col, Button } from 'react-bootstrap';
import { motion, useAnimation, useInView } from 'framer-motion';
import { Helmet } from 'react-helmet';
import FAQSection from './FAQSection';

const NetSuiteERPLanding = () => {
  const [hoveredBenefit, setHoveredBenefit] = useState(null);
  const controls = useAnimation();
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true });

  useEffect(() => {
    if (isInView) {
      controls.start("visible");
    }
  }, [controls, isInView]);

  const benefits = [
    {
      title: "Automate Business Processes",
      description: "Eliminate human errors through automation of crucial business procedures",
      icon: "bi-robot",
      color: "#4fc3f7"
    },
    {
      title: "Real-time Insights",
      description: "Achieve instant financial and operational visibility across teams",
      icon: "bi-speedometer2",
      color: "#66bb6a"
    },
    {
      title: "Unified Platform",
      description: "Consolidate accounting, inventory, and order management in one system",
      icon: "bi-layers",
      color: "#ffa726"
    },
    {
      title: "Scalable Cloud Architecture",
      description: "Expand easily with infrastructure designed for business growth",
      icon: "bi-cloud-arrow-up",
      color: "#ec407a"
    },
    {
      title: "Data Security",
      description: "Guarantee safety and compliance with enterprise-grade protection",
      icon: "bi-shield-lock",
      color: "#26c6da"
    }
  ];

  const features = [
    { name: "Accounting", icon: "bi-calculator", color: "#4fc3f7" },
    { name: "Integration", icon: "bi-plug", color: "#66bb6a" },
    { name: "Automation", icon: "bi-gear-wide-connected", color: "#ffa726" },
    { name: "Reporting", icon: "bi-graph-up", color: "#ec407a" },
    { name: "Manufacturing", icon: "bi-cpu", color: "#26c6da" },
    { name: "Data Analytics", icon: "bi-bar-chart-line", color: "#7e57c2" },
    { name: "Tracking & Visibility", icon: "bi-eye", color: "#4fc3f7" },
    { name: "Sales & Marketing", icon: "bi-megaphone", color: "#66bb6a" },
    { name: "Human Resource", icon: "bi-people", color: "#ffa726" },
    { name: "Financial Management", icon: "bi-cash-stack", color: "#ec407a" },
    { name: "Supply Chain", icon: "bi-truck", color: "#26c6da" },
    { name: "CRM", icon: "bi-person-lines-fill", color: "#7e57c2" }
  ];

  const fadeInUp = {
    hidden: { opacity: 0, y: 50 },
    visible: { 
      opacity: 1, 
      y: 0,
      transition: { duration: 0.6, ease: "easeOut" }
    }
  };

  const staggerContainer = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
        delayChildren: 0.3
      }
    }
  };

  const pulseAnimation = {
    hidden: { scale: 0.8, opacity: 0 },
    visible: {
      scale: [1, 1.05, 1],
      opacity: 1,
      transition: {
        duration: 1.5,
        repeat: Infinity,
        repeatType: "reverse"
      }
    }
  };



  return (
    <div style={{
      overflowX: 'hidden',
      fontFamily: "'Segoe UI', Tahoma, Geneva, Verdana, sans-serif"
    }}>
      <Helmet>
        <title>NetSuite ERP Solutions USA | SpireNest</title>
        <link rel="canonical" href="https://spirenest.co/netsuite/services/erp" />
        <meta name="description" content="Transform your business with NetSuite ERP solutions from SpireNest. Comprehensive ERP implementation and support for US businesses." />
        <meta name="keywords" content="NetSuite ERP, ERP solutions, business automation, USA, SpireNest" />
      </Helmet>
      {/* Animated Hero Section */}
      <section style={{
        background: 'linear-gradient(135deg, #1a237e 0%, #283593 100%)',
        color: 'white',
        padding: '8rem 0 10rem',
        position: 'relative',
        overflow: 'hidden'
      }}>
        
        
        {/* Floating particles animation */}
        {[...Array(20)].map((_, i) => (
          <motion.div
            key={i}
            style={{
              position: 'absolute',
              background: 'rgba(255,255,255,0.6)',
              borderRadius: '50%',
              width: `${Math.random() * 5 + 2}px`,
              height: `${Math.random() * 5 + 2}px`,
              top: `${Math.random() * 100}%`,
              left: `${Math.random() * 100}%`,
            }}
            animate={{
              y: [0, (Math.random() * 200) - 100],
              x: [0, (Math.random() * 200) - 100],
              opacity: [0.3, 0.8, 0.3],
            }}
            transition={{
              duration: Math.random() * 10 + 10,
              repeat: Infinity,
              repeatType: "reverse",
              ease: "linear"
            }}
          />
        ))}
        
        <Container>
          <Row className="align-items-center">
            <Col lg={6} className="mb-5 mb-lg-0">
              <motion.div
                initial="hidden"
                animate="visible"
                variants={staggerContainer}
              >
                <motion.h1 
                  className="display-3 fw-bold mb-4" 
                  style={{ 
                    textShadow: '2px 2px 4px rgba(0,0,0,0.3)',
                    lineHeight: '1.2'
                  }}
                  variants={fadeInUp}
                >
                  Oracle <span style={{ color: '#4fc3f7' }}>NetSuite ERP</span> Solutions
                </motion.h1>
                <motion.p 
                  className="lead mb-4" 
                  style={{ fontSize: '1.5rem' }}
                  variants={fadeInUp}
                >
                  Elevate your enterprise with the leading cloud-based ERP platform designed for scalability, automation, and informed decision-making.
                </motion.p>
                <motion.div 
                  className="d-flex flex-wrap gap-3"
                  variants={fadeInUp}
                >
                  <Button 
                    variant="light" 
                    size="lg" 
                    className="rounded-pill px-4 py-3 fw-bold"
                    style={{
                      background: 'white',
                      color: '#1a237e',
                      boxShadow: '0 4px 15px rgba(0,0,0,0.2)',
                      transition: 'all 0.3s ease',
                      minWidth: '220px'
                    }}
                    whileHover={{ 
                      y: -5,
                      scale: 1.05,
                      boxShadow: '0 8px 25px rgba(0,0,0,0.3)'
                    }}
                  >
                     <a href="/contact" className="btn ">
                     Talk to Our Experts
                </a>
                    
                    
                  </Button>
                  
                </motion.div>
              </motion.div>
            </Col>
            <Col lg={6}>
              <div style={{
                position: 'relative',
                height: '400px',
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center'
              }}>
                <motion.div
                  style={{
                    position: 'absolute',
                    width: '350px',
                    height: '350px',
                    borderRadius: '50%',
                    border: '15px solid rgba(79, 195, 247, 0.3)',
                  }}
                  animate={{
                    rotate: 360,
                  }}
                  transition={{
                    duration: 20,
                    repeat: Infinity,
                    ease: "linear"
                  }}
                />
                <motion.div
                  style={{
                    position: 'absolute',
                    width: '300px',
                    height: '300px',
                    borderRadius: '50%',
                    border: '15px solid rgba(76, 175, 80, 0.3)',
                  }}
                  animate={{
                    rotate: -360,
                  }}
                  transition={{
                    duration: 15,
                    repeat: Infinity,
                    ease: "linear"
                  }}
                />
                <motion.div
                  style={{
                    position: 'absolute',
                    width: '250px',
                    height: '250px',
                    borderRadius: '50%',
                    border: '15px solid rgba(255, 193, 7, 0.3)',
                  }}
                  animate={{
                    rotate: 360,
                  }}
                  transition={{
                    duration: 10,
                    repeat: Infinity,
                    ease: "linear"
                  }}
                />
                <motion.div
                  style={{
                    position: 'absolute',
                    width: '200px',
                    height: '200px',
                    borderRadius: '50%',
                    background: 'rgba(255,255,255,0.9)',
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    boxShadow: '0 10px 30px rgba(0,0,0,0.2)',
                  }}
                  animate={{
                    y: [-15, 15, -15],
                  }}
                  transition={{
                    duration: 6,
                    repeat: Infinity,
                    ease: "easeInOut"
                  }}
                >
                  <div style={{
                    textAlign: 'center',
                    padding: '1rem'
                  }}>
                    <div style={{
                      fontSize: '2rem',
                      fontWeight: 'bold',
                      color: '#1a237e',
                      marginBottom: '0.5rem'
                    }}>NetSuite</div>
                    <div style={{
                      fontSize: '1rem',
                      color: '#666'
                    }}>Cloud ERP Platform</div>
                  </div>
                </motion.div>
              </div>
            </Col>
          </Row>
        </Container>
      </section>

      {/* Benefits Section */}
      <section style={{ 
        padding: '6rem 0',
        background: 'linear-gradient(to bottom, #f8f9fa, #ffffff)',
        position: 'relative',
        overflow: 'hidden'
      }}>
        {/* Animated background elements */}
        <motion.div
          style={{
            position: 'absolute',
            top: '20%',
            left: '10%',
            width: '100px',
            height: '100px',
            borderRadius: '50%',
            background: 'rgba(79, 195, 247, 0.1)',
            filter: 'blur(30px)'
          }}
          animate={{
            scale: [1, 1.5, 1],
            opacity: [0.3, 0.6, 0.3]
          }}
          transition={{
            duration: 8,
            repeat: Infinity,
            ease: "easeInOut"
          }}
        />
        <motion.div
          style={{
            position: 'absolute',
            bottom: '30%',
            right: '15%',
            width: '150px',
            height: '150px',
            borderRadius: '50%',
            background: 'rgba(102, 187, 106, 0.1)',
            filter: 'blur(40px)'
          }}
          animate={{
            scale: [1, 1.8, 1],
            opacity: [0.2, 0.5, 0.2]
          }}
          transition={{
            duration: 10,
            repeat: Infinity,
            ease: "easeInOut",
            delay: 2
          }}
        />
        
        <Container>
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={staggerContainer}
            className="text-center mb-5"
          >
            <motion.div variants={fadeInUp}>
              <h2 className="fw-bold mb-3" style={{ 
                color: '#1a237e',
                fontSize: '2.5rem'
              }}>
                Why Choose <span style={{ color: '#4fc3f7' }}>NetSuite ERP?</span>
              </h2>
              <p style={{ 
                fontSize: '1.25rem', 
                color: '#64748b',
                maxWidth: '700px',
                margin: '0 auto'
              }}>
                Discover how NetSuite can transform your business operations with these powerful benefits
              </p>
            </motion.div>
          </motion.div>
          
          <Row className="g-4 justify-content-center">
            {benefits.map((benefit, index) => (
              <Col md={6} lg={4} key={index}>
                <motion.div
                  initial={{ opacity: 0, y: 50 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  viewport={{ once: true }}
                  style={{
                    textAlign: 'left',
                    padding: '2rem',
                    background: 'white',
                    borderRadius: '16px',
                    boxShadow: '0 10px 30px rgba(0,0,0,0.05)',
                    height: '100%',
                    borderBottom: `4px solid ${benefit.color}`,
                    transition: 'all 0.4s ease',
                    cursor: 'pointer'
                  }}
                  whileHover={{ 
                    y: -10,
                    boxShadow: `0 15px 40px ${benefit.color}20`
                  }}
                  onHoverStart={() => setHoveredBenefit(index)}
                  onHoverEnd={() => setHoveredBenefit(null)}
                >
                  <div 
                    className="d-flex align-items-center mb-3"
                  >
                    <motion.div 
                      style={{
                        width: '60px',
                        height: '60px',
                        borderRadius: '15px',
                        background: hoveredBenefit === index ? benefit.color : '#f5f5f5',
                        color: hoveredBenefit === index ? 'white' : benefit.color,
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        fontSize: '1.8rem',
                        marginRight: '1rem',
                        transition: 'all 0.3s ease'
                      }}
                      animate={{
                        rotate: hoveredBenefit === index ? [0, 10, -10, 0] : 0
                      }}
                      transition={{
                        duration: 0.5
                      }}
                    >
                      <i className={`bi ${benefit.icon}`}></i>
                    </motion.div>
                    <h4 className="mb-0" style={{ 
                      color: hoveredBenefit === index ? benefit.color : '#1a237e',
                      transition: 'all 0.3s ease'
                    }}>
                      {benefit.title}
                    </h4>
                  </div>
                  <p style={{ 
                    color: '#5c6bc0',
                    transition: 'all 0.3s ease',
                    flex: 1
                  }}>
                    {benefit.description}
                  </p>
                  <motion.div
                    variants={pulseAnimation}
                    initial="hidden"
                    animate="visible"
                    style={{
                      width: '40px',
                      height: '4px',
                      background: benefit.color,
                      borderRadius: '2px',
                      marginTop: '1rem'
                    }}
                  />
                </motion.div>
              </Col>
            ))}
          </Row>
        </Container>
      </section>

      {/* Features Section - Marquee */}
      <section style={{ 
        padding: '6rem 0',
        background: 'linear-gradient(135deg, #e3f2fd 0%, #bbdefb 100%)',
        position: 'relative',
        overflow: 'hidden'
      }}>
        {/* Animated background elements */}
        <motion.div
          style={{
            position: 'absolute',
            top: '50%',
            left: '5%',
            width: '200px',
            height: '200px',
            borderRadius: '50%',
            background: 'rgba(79, 195, 247, 0.2)',
            filter: 'blur(50px)'
          }}
          animate={{
            x: [0, 100, 0],
            y: [0, -50, 0],
            opacity: [0.3, 0.7, 0.3]
          }}
          transition={{
            duration: 12,
            repeat: Infinity,
            ease: "easeInOut"
          }}
        />
        <motion.div
          style={{
            position: 'absolute',
            bottom: '20%',
            right: '10%',
            width: '300px',
            height: '300px',
            borderRadius: '50%',
            background: 'rgba(26, 35, 126, 0.1)',
            filter: 'blur(60px)'
          }}
          animate={{
            scale: [1, 1.5, 1],
            opacity: [0.2, 0.5, 0.2]
          }}
          transition={{
            duration: 15,
            repeat: Infinity,
            ease: "easeInOut",
            delay: 3
          }}
        />
        
        <Container style={{ position: 'relative', zIndex: 1 }}>
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={staggerContainer}
            className="text-center mb-5"
          >
            <motion.div variants={fadeInUp}>
              <h2 className="fw-bold mb-3" style={{ 
                color: '#1a237e',
                fontSize: '2.5rem'
              }}>
                Core <span style={{ color: '#4fc3f7' }}>Features</span>
              </h2>
              <p style={{ 
                fontSize: '1.25rem', 
                color: '#64748b',
                maxWidth: '700px',
                margin: '0 auto'
              }}>
                Explore the comprehensive features that make NetSuite the leading ERP solution
              </p>
            </motion.div>
          </motion.div>
          
          {/* Marquee Container */}
          <div style={{
            width: '100%',
            overflow: 'hidden',
            position: 'relative',
            maskImage: 'linear-gradient(90deg, transparent 0%, black 10%, black 90%, transparent 100%)',
            WebkitMaskImage: 'linear-gradient(90deg, transparent 0%, black 10%, black 90%, transparent 100%)'
          }}>
            <motion.div
              style={{
                display: 'flex',
                gap: window.innerWidth < 768 ? '1rem' : '2rem',
                width: 'fit-content'
              }}
              animate={{
                x: [0, -((features.length * (window.innerWidth < 768 ? 180 : 220)) + (features.length * (window.innerWidth < 768 ? 16 : 32)))]
              }}
              transition={{
                duration: window.innerWidth < 768 ? 25 : 20,
                repeat: Infinity,
                ease: "linear"
              }}
            >
              {/* First set of features */}
              {features.map((feature, index) => (
                <motion.div
                  key={`first-${index}`}
                  className="p-4 rounded-4 text-center"
                  style={{
                    background: 'rgba(255,255,255,0.9)',
                    boxShadow: '0 8px 25px rgba(0,0,0,0.1)',
                    border: '1px solid rgba(0,0,0,0.05)',
                    minWidth: window.innerWidth < 768 ? '180px' : '220px',
                    flexShrink: 0
                  }}
                  whileHover={{ 
                    scale: 1.05,
                    boxShadow: `0 15px 35px ${feature.color}30`
                  }}
                >
                  <motion.div 
                    style={{
                      width: window.innerWidth < 768 ? '60px' : '70px',
                      height: window.innerWidth < 768 ? '60px' : '70px',
                      borderRadius: '50%',
                      background: `linear-gradient(135deg, ${feature.color}, #1a237e)`,
                      color: 'white',
                      display: 'flex',
                      justifyContent: 'center',
                      alignItems: 'center',
                      fontSize: window.innerWidth < 768 ? '1.5rem' : '1.8rem',
                      margin: '0 auto 1rem',
                      boxShadow: `0 8px 20px ${feature.color}40`
                    }}
                    whileHover={{
                      scale: 1.1,
                      rotate: 15
                    }}
                  >
                    <i className={`bi ${feature.icon}`}></i>
                  </motion.div>
                  <h5 style={{ 
                    color: '#1a237e',
                    fontSize: window.innerWidth < 768 ? '0.95rem' : '1.1rem',
                    fontWeight: 'bold'
                  }}>
                    {feature.name}
                  </h5>
                </motion.div>
              ))}
              
              {/* Duplicate set for seamless loop */}
              {features.map((feature, index) => (
                <motion.div
                  key={`duplicate-${index}`}
                  className="p-4 rounded-4 text-center"
                  style={{
                    background: 'rgba(255,255,255,0.9)',
                    boxShadow: '0 8px 25px rgba(0,0,0,0.1)',
                    border: '1px solid rgba(0,0,0,0.05)',
                    minWidth: window.innerWidth < 768 ? '180px' : '220px',
                    flexShrink: 0
                  }}
                  whileHover={{ 
                    scale: 1.05,
                    boxShadow: `0 15px 35px ${feature.color}30`
                  }}
                >
                  <motion.div 
                    style={{
                      width: window.innerWidth < 768 ? '60px' : '70px',
                      height: window.innerWidth < 768 ? '60px' : '70px',
                      borderRadius: '50%',
                      background: `linear-gradient(135deg, ${feature.color}, #1a237e)`,
                      color: 'white',
                      display: 'flex',
                      justifyContent: 'center',
                      alignItems: 'center',
                      fontSize: window.innerWidth < 768 ? '1.5rem' : '1.8rem',
                      margin: '0 auto 1rem',
                      boxShadow: `0 8px 20px ${feature.color}40`
                    }}
                    whileHover={{
                      scale: 1.1,
                      rotate: 15
                    }}
                  >
                    <i className={`bi ${feature.icon}`}></i>
                  </motion.div>
                  <h5 style={{ 
                    color: '#1a237e',
                    fontSize: window.innerWidth < 768 ? '0.95rem' : '1.1rem',
                    fontWeight: 'bold'
                  }}>
                    {feature.name}
                  </h5>
                </motion.div>
              ))}
            </motion.div>
          </div>
        </Container>
      </section>

      {/* Business Revolution Section */}
      <section style={{ padding: '6rem 0', background: 'white' }}>
        <Container>
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={staggerContainer}
            className="text-center mb-5"
          >
            <motion.div variants={fadeInUp}>
              <h2 className="fw-bold mb-3" style={{ 
                color: '#1a237e',
                fontSize: '2.5rem'
              }}>
                Revolutionize Your <span style={{ color: '#4fc3f7' }}>Business</span>
              </h2>
              <p style={{ 
                fontSize: '1.25rem', 
                color: '#64748b',
                maxWidth: '700px',
                margin: '0 auto'
              }}>
                Transform your operations with NetSuite's powerful capabilities
              </p>
            </motion.div>
          </motion.div>
          
          <Row className="align-items-center mb-5 g-4">
            <Col lg={6} className="mb-5 mb-lg-0">
              <motion.div
                initial={{ opacity: 0, x: -50 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.6 }}
                viewport={{ once: true }}
                className="p-4 rounded-4 h-100"
                style={{
                  background: 'linear-gradient(135deg, #f8f9fa, #ffffff)',
                  boxShadow: '0 10px 30px rgba(0,0,0,0.05)',
                  height: '100%',
                  borderLeft: '4px solid #4fc3f7'
                }}
              >
                <h3 className="mb-4" style={{ color: '#1a237e' }}>Streamline Financial Processes</h3>
                <ul className="list-unstyled">
                  {[
                    "Enhance efficiency and productivity through automation",
                    "Maintain optimal inventory levels and minimize costs",
                    "Exercise precise control over supply chain dynamics",
                    "Safeguard order management from errors"
                  ].map((item, index) => (
                    <motion.li 
                      key={index}
                      className="mb-3 d-flex"
                      initial={{ opacity: 0, x: -20 }}
                      whileInView={{ opacity: 1, x: 0 }}
                      transition={{ delay: index * 0.1 }}
                      viewport={{ once: true }}
                    >
                      <div style={{
                        width: '30px',
                        height: '30px',
                        borderRadius: '50%',
                        background: '#4fc3f7',
                        color: 'white',
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        marginRight: '1rem',
                        flexShrink: 0
                      }}>
                        <i className="bi bi-check-lg"></i>
                      </div>
                      <span>{item}</span>
                    </motion.li>
                  ))}
                </ul>
              </motion.div>
            </Col>
            <Col lg={6}>
              <motion.div
                initial={{ opacity: 0, x: 50 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.6 }}
                viewport={{ once: true }}
                className="p-4 rounded-4 h-100"
                style={{
                  background: 'linear-gradient(135deg, #f8f9fa, #ffffff)',
                  boxShadow: '0 10px 30px rgba(0,0,0,0.05)',
                  height: '100%',
                  borderLeft: '4px solid #66bb6a'
                }}
              >
                <h3 className="mb-4" style={{ color: '#1a237e' }}>Comprehensive ERP Solutions</h3>
                <ul className="list-unstyled">
                  {[
                    "Seamlessly integrate financial management with business intelligence",
                    "Enhance planning, budgeting, and forecasting",
                    "Streamline sales, finance, and fulfillment processes",
                    "Optimize supply chain and inventory on a unified platform"
                  ].map((item, index) => (
                    <motion.li 
                      key={index}
                      className="mb-3 d-flex"
                      initial={{ opacity: 0, x: 20 }}
                      whileInView={{ opacity: 1, x: 0 }}
                      transition={{ delay: index * 0.1 }}
                      viewport={{ once: true }}
                    >
                      <div style={{
                        width: '30px',
                        height: '30px',
                        borderRadius: '50%',
                        background: '#66bb6a',
                        color: 'white',
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        marginRight: '1rem',
                        flexShrink: 0
                      }}>
                        <i className="bi bi-check-lg"></i>
                      </div>
                      <span>{item}</span>
                    </motion.li>
                  ))}
                </ul>
              </motion.div>
            </Col>
          </Row>
        </Container>
      </section>

      {/* Partner Section */}
      <section style={{ 
        padding: '6rem 0',
        background: 'linear-gradient(135deg, #1a237e 0%, #283593 100%)',
        color: 'white',
        position: 'relative',
        overflow: 'hidden'
      }}>
        {/* Animated floating shapes */}
        <motion.div
          style={{
            position: 'absolute',
            top: '20%',
            left: '5%',
            width: '100px',
            height: '100px',
            borderRadius: '30% 70% 70% 30% / 30% 30% 70% 70%',
            background: 'rgba(255,255,255,0.1)',
            border: '1px solid rgba(255,255,255,0.2)'
          }}
          animate={{
            y: [0, 50, 0],
            rotate: [0, 180, 360],
            borderRadius: [
              '30% 70% 70% 30% / 30% 30% 70% 70%',
              '60% 40% 30% 70% / 60% 30% 70% 40%',
              '30% 70% 70% 30% / 30% 30% 70% 70%'
            ]
          }}
          transition={{
            duration: 15,
            repeat: Infinity,
            ease: "easeInOut"
          }}
        />
        <motion.div
          style={{
            position: 'absolute',
            bottom: '15%',
            right: '10%',
            width: '150px',
            height: '150px',
            borderRadius: '60% 40% 30% 70% / 60% 30% 70% 40%',
            background: 'rgba(255,255,255,0.1)',
            border: '1px solid rgba(255,255,255,0.2)'
          }}
          animate={{
            y: [0, -80, 0],
            rotate: [0, -180, -360],
            borderRadius: [
              '60% 40% 30% 70% / 60% 30% 70% 40%',
              '30% 70% 70% 30% / 30% 30% 70% 70%',
              '60% 40% 30% 70% / 60% 30% 70% 40%'
            ]
          }}
          transition={{
            duration: 20,
            repeat: Infinity,
            ease: "easeInOut",
            delay: 5
          }}
        />
        
        <Container>
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={staggerContainer}
            className="text-center mb-5"
          >
            <motion.div variants={fadeInUp}>
              <h2 className="fw-bold mb-3" style={{ fontSize: '2.5rem' }}>
                Your Partner in the <span style={{ color: '#4fc3f7' }}>Journey to Success</span>
              </h2>
              <p style={{ 
                fontSize: '1.25rem', 
                opacity: 0.8,
                maxWidth: '700px',
                margin: '0 auto'
              }}>
                We're with you every step of the way in your digital transformation
              </p>
            </motion.div>
          </motion.div>
          
          <Row className="g-4">
            {[
              {
                title: "Business Intelligence",
                description: "Utilize built-in BI modules for powerful data integration and impactful visual analytics",
                icon: "bi-graph-up",
                color: "#4fc3f7"
              },
              {
                title: "Scalable Growth",
                description: "Leverage NetSuite's scalable system to support unrestricted business growth",
                icon: "bi-arrow-up-right",
                color: "#66bb6a"
              },
              {
                title: "Implementation Support",
                description: "Get thorough training and continuous support for seamless system adoption",
                icon: "bi-headset",
                color: "#ffa726"
              }
            ].map((item, index) => (
              <Col md={4} key={index}>
                <motion.div
                  initial={{ opacity: 0, y: 50 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  viewport={{ once: true }}
                  className="h-100 p-4 rounded-4"
                  style={{
                    background: 'rgba(255,255,255,0.1)',
                    border: '1px solid rgba(255,255,255,0.2)',
                    height: '100%',
                    transition: 'all 0.3s ease'
                  }}
                  whileHover={{
                    background: 'rgba(255,255,255,0.15)',
                    borderColor: item.color,
                    y: -5
                  }}
                >
                  <div className="d-flex align-items-center mb-3">
                    <motion.div 
                      style={{
                        width: '50px',
                        height: '50px',
                        borderRadius: '50%',
                        background: 'white',
                        color: '#1a237e',
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        fontSize: '1.5rem',
                        marginRight: '1rem',
                        flexShrink: 0
                      }}
                      whileHover={{
                        rotate: 15,
                        scale: 1.1
                      }}
                    >
                      <i className={`bi ${item.icon}`}></i>
                    </motion.div>
                    <h4 className="mb-0">{item.title}</h4>
                  </div>
                  <p>{item.description}</p>
                  <motion.div
                    variants={pulseAnimation}
                    initial="hidden"
                    animate="visible"
                    style={{
                      width: '40px',
                      height: '4px',
                      background: item.color,
                      borderRadius: '2px',
                      marginTop: '1rem'
                    }}
                  />
                </motion.div>
              </Col>
            ))}
          </Row>
        </Container>
      </section>

      {/* FAQ Section */}
      <FAQSection />
    </div>
  );
};

export default NetSuiteERPLanding;